'use es6';

import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Map as ImmutableMap } from 'immutable';
import { refreshTrackingStatus } from 'AdsUI/data/TrackingStatusData';
import Raven from 'raven-js';
export const MAX_CONCURRENT_REFRESH = 10;
const SYNC_QUEUED = 'SYNC_QUEUED';
const SYNC_START = 'SYNC_START';
const SYNC_SUCCESS = 'SYNC_SUCCESS';
const SYNC_FAILED = 'SYNC_FAILED';
export const useRefreshStatuses = () => {
  const [refreshState, refreshDispatch] = useReducer((state, action) => {
    const {
      payload: {
        adNetwork,
        adAccountId,
        id
      },
      type
    } = action;

    switch (type) {
      case SYNC_QUEUED:
        return state.setIn([adNetwork, adAccountId, id], {
          queued: true,
          loading: true,
          error: false
        });

      case SYNC_START:
        return state.setIn([adNetwork, adAccountId, id], {
          loading: true,
          error: false
        });

      case SYNC_SUCCESS:
        return state.setIn([adNetwork, adAccountId, id], {
          loading: false,
          error: false,
          success: true,
          successAt: Date.now()
        });

      case SYNC_FAILED:
        return state.setIn([adNetwork, adAccountId, id], {
          loading: false,
          error: true
        });

      default:
        return state;
    }
  }, ImmutableMap());
  const concurrentRefreshes = useMemo(() => refreshState.reduce((total, accounts) => total + accounts.reduce((accountSum, refreshStatuses) => accountSum + refreshStatuses.filter(({
    queued,
    loading: refreshLoading
  }) => refreshLoading && !queued).size, 0), 0), [refreshState]);
  const [refreshQueue, setRefreshQueue] = useState([]);
  useEffect(() => {
    if (concurrentRefreshes >= MAX_CONCURRENT_REFRESH || !refreshQueue.length) {
      return;
    }

    const entity = refreshQueue.shift();
    const {
      adNetwork,
      adAccountId,
      id
    } = entity;
    setRefreshQueue(refreshQueue);
    refreshDispatch({
      type: SYNC_START,
      payload: entity
    });
    refreshTrackingStatus(adNetwork, adAccountId, id).then(({
      isTracked
    }) => {
      if (isTracked) {
        refreshDispatch({
          type: SYNC_SUCCESS,
          payload: entity
        });
      } else {
        refreshDispatch({
          type: SYNC_FAILED,
          payload: entity
        });
      }
    }).catch(e => {
      refreshDispatch({
        type: SYNC_FAILED,
        payload: entity
      });
      Raven.captureException(e);
    });
  }, [refreshDispatch, refreshQueue, setRefreshQueue, concurrentRefreshes]);
  const handleRefresh = useCallback(entities => {
    entities.forEach(entity => refreshDispatch({
      type: SYNC_QUEUED,
      payload: entity
    }));
    setRefreshQueue([...refreshQueue, ...entities]);
  }, [refreshQueue, setRefreshQueue]);
  const handleMarkRefreshSucceeded = useCallback(entity => refreshDispatch({
    type: SYNC_SUCCESS,
    payload: entity
  }), [refreshDispatch]);
  const handleMarkRefreshFailed = useCallback(entity => refreshDispatch({
    type: SYNC_FAILED,
    payload: entity
  }), [refreshDispatch]);
  return [refreshState, concurrentRefreshes, handleRefresh, handleMarkRefreshSucceeded, handleMarkRefreshFailed];
};
export default useRefreshStatuses;